import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import { ReactComponent as CloseIcon } from 'assets/iconButtons/new-close-icon.svg';
import { ReactComponent as ArrowUpIcon } from 'assets/iconButtons/new-arrow-up-icon.svg';
import { ReactComponent as CopyIcon } from 'assets/iconButtons/new-copy-icon.svg';
import { ReactComponent as ExportIcon } from 'assets/iconButtons/new-export-icon.svg';
import { CUSTOMER_PERMISSIONS, TRANSACTION_TYPES } from 'components/common/constants';
import i18nContext from 'components/i18n-context';
import { useMediaQuery } from 'services/useMediaQUery';
import { MEDIA_QUERY } from 'services/utils';
import { formatPaymentDateSeparate } from 'services/utils';
import Button from 'uikit/Button/Button';
import FileDisplay from 'components/common/FileDisplay';
import './CommissionPaymentDetails.scss';

const CommissionPaymentDetails = ({
  transaction,
  amountDetails,
  commissionDetails,
  totalAmountDetails,
  onClose,
  accountNumber,
  userAccounts,
  transactionStore,
  customerPermissions,
  userStore
}) => {
  const navigate = useNavigate();
  const i18n = useContext(i18nContext);
  const isMobile = useMediaQuery(MEDIA_QUERY.MOBILE);
  const [copiedField, setCopiedField] = useState(null);

  const isReportDisabled = customerPermissions
    ? !customerPermissions?.find((permission) => permission === CUSTOMER_PERMISSIONS.CREATE_REPORT)
    : false;

  const handleCopy = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const onPDFClick = (e) => {
    e.preventDefault();
    transactionStore.downloadCurrentPDF(transaction.account_number, transaction.transaction_number);
  };

  // Format payment purpose with key-value display
  const getFormattedPurpose = () => {
    if (!transaction.purpose || !userStore?.paymentPurposes) {
      return transaction.purpose;
    }
    const purposeValue = userStore.paymentPurposes[transaction.purpose];
    return purposeValue ? `${transaction.purpose} - ${purposeValue}` : transaction.purpose;
  };
  const formattedPurpose = getFormattedPurpose();

  const getStatusBadgeClass = (status) => {
    const statusClassMap = {
      COMPLETED: 'status-completed',
      PAID: 'status-completed',
      IN_PROGRESS: 'status-in-progress',
      PROCESSING: 'status-in-progress',
      PENDING: 'status-pending',
      CREATED: 'status-pending',
      ON_HOLD: 'status-on-hold',
      FAILED: 'status-failed',
      REJECTED: 'status-failed',
      MONITORING: 'status-monitoring',
      CANCELLED: 'status-cancelled',
      ACCEPT_REQUESTED: 'status-requested',
      REJECT_REQUESTED: 'status-requested'
    };
    return statusClassMap[status] || 'status-default';
  };

  const createdDate = formatPaymentDateSeparate(transaction.created_at);
  const completedDate = formatPaymentDateSeparate(transaction.completed_at);
  const lastUpdatedDate = formatPaymentDateSeparate(transaction.updated_at);

  // Calculate amounts
  const isOutgoingTransaction = transaction.type === TRANSACTION_TYPES.OUTGOING;
  const isIncomingTransaction = transaction.type === TRANSACTION_TYPES.INCOMING;
  const isFailedOrRejected = transaction.status === 'FAILED' || transaction.status === 'REJECTED';
  const displayStatus = transaction.status === 'PAID' ? 'COMPLETED' : transaction.status;
  const amount = `${isOutgoingTransaction ? `-${transaction?.amountDetails?.displayAmount}` : isIncomingTransaction ? `+${transaction?.sourceAmountDetails?.displayAmount || transaction?.amountDetails?.displayAmount}` : ''}`;
  const commission = transaction?.totalCommissionDetails?.displayAmount;

  const renderMobile = () => (
    <div className='commission-payment-details mobile'>
      <div className='modal-header'>
        <h2 className='modal-title'>{i18n.getMessage('popUp.paymentDetails')}</h2>
        <CloseIcon className='close-icon' onClick={onClose} />
      </div>

      <div className='modal-body mobile'>
        {/* Payment Summary */}
        <div className='payment-summary'>
          <div className='transaction-type-block'>
            <div className='transaction-type'>
              <div className='type-icon-wrapper'>
                <ArrowUpIcon className='type-icon' />
              </div>
              <span className='type-label'>{i18n.getMessage('transaction.type.commission')}</span>
            </div>
            <div className='transaction-date'>{`${createdDate.date}, ${createdDate.time}`}</div>
          </div>

          <div className='amount-status'>
            <span className={`status-badge ${getStatusBadgeClass(displayStatus)}`}>
              {i18n.getMessage(`transaction.details.status.${displayStatus}`)}
            </span>
            <div
              className={`transaction-amount ${isIncomingTransaction ? 'incoming' : ''} ${isFailedOrRejected ? 'failed' : ''}`}
            >
              {amount}
            </div>
          </div>
        </div>

        {/* Transaction Details */}
        <div className='details-section mobile'>
          <h3 className='section-title'>{i18n.getMessage('transaction.section.transactionDetails')}</h3>

          <div className='detail-row'>
            <span className='detail-label'>{i18n.getMessage('transaction.label.reference')}</span>
            <div className='detail-value'>
              <span>{transaction.comment}</span>
              <button className='copy-button' onClick={() => handleCopy(transaction.comment, 'reference')}>
                <CopyIcon />
              </button>
            </div>
          </div>

          {transaction.purpose && (
            <div className='detail-row'>
              <span className='detail-label'>{i18n.getMessage('transaction.label.purpose')}</span>
              <div className='detail-value'>
                <span>{formattedPurpose}</span>
                <button className='copy-button' onClick={() => handleCopy(formattedPurpose, 'purpose')}>
                  <CopyIcon />
                </button>
              </div>
            </div>
          )}

          <div className='detail-row'>
            <span className='detail-label'>{i18n.getMessage('transaction.label.commission')}</span>
            <div className='detail-value'>
              <span>{commission}</span>
            </div>
          </div>

          <div className='detail-row'>
            <span className='detail-label'>{i18n.getMessage('transaction.label.transactionId')}</span>
            <div className='detail-value'>
              <span>{transaction.transaction_number}</span>
              <button
                className='copy-button'
                onClick={() => handleCopy(transaction.transaction_number, 'transactionId')}
              >
                <CopyIcon />
              </button>
            </div>
          </div>

          <div className='detail-row'>
            <span className='detail-label'>{i18n.getMessage('transaction.label.paymentMethod')}</span>
            <div className='detail-value'>
              <span>{i18n.getMessage(`transfer.paymentMethod.${transaction.payment_method}`)}</span>
            </div>
          </div>

          <div className='detail-row'>
            <span className='detail-label'>{i18n.getMessage('transaction.label.dateCreated')}</span>
            <div className='detail-value'>
              <span>{`${createdDate.date}, ${createdDate.time}`}</span>
            </div>
          </div>

          {completedDate.date && completedDate.time && (
            <div className='detail-row'>
              <span className='detail-label'>{i18n.getMessage('transaction.label.dateCompleted')}</span>
              <div className='detail-value'>
                <span>{`${completedDate.date}, ${completedDate.time}`}</span>
              </div>
            </div>
          )}

          <div className='detail-row'>
            <span className='detail-label'>{i18n.getMessage('transaction.label.lastUpdated')}</span>
            <div className='detail-value'>
              <span>{`${lastUpdatedDate.date}, ${lastUpdatedDate.time}`}</span>
            </div>
          </div>
        </div>

        {/* First Section - Sender for Incoming, Recipient for Outgoing */}
        <div className='details-section mobile'>
          <h3 className='section-title'>
            {i18n.getMessage(
              isIncomingTransaction ? 'transaction.section.senderDetails' : 'transaction.section.recipientDetails'
            )}
          </h3>

          {(isIncomingTransaction ? transaction.sender_name : transaction.recipient_name) && (
            <div className='detail-row'>
              <span className='detail-label'>{i18n.getMessage('transaction.label.name')}</span>
              <div className='detail-value'>
                <span>{isIncomingTransaction ? transaction.sender_name : transaction.recipient_name}</span>
                <button
                  className='copy-button'
                  onClick={() =>
                    handleCopy(
                      isIncomingTransaction ? transaction.sender_name : transaction.recipient_name,
                      isIncomingTransaction ? 'senderName' : 'recipientName'
                    )
                  }
                >
                  <CopyIcon />
                </button>
              </div>
            </div>
          )}

          {(isIncomingTransaction
            ? transaction.counterparty?.iban
            : transaction.recipient_wallet_number || transaction.counterparty?.iban) && (
            <div className='detail-row'>
              <span className='detail-label'>{i18n.getMessage('transaction.label.accountNumber')}</span>
              <div className='detail-value'>
                <span>
                  {isIncomingTransaction
                    ? transaction.counterparty?.iban
                    : transaction.recipient_wallet_number || transaction.counterparty?.iban}
                </span>
                <button
                  className='copy-button'
                  onClick={() =>
                    handleCopy(
                      isIncomingTransaction
                        ? transaction.counterparty?.iban
                        : transaction.recipient_wallet_number || transaction.counterparty?.iban,
                      isIncomingTransaction ? 'senderAccount' : 'recipientAccount'
                    )
                  }
                >
                  <CopyIcon />
                </button>
              </div>
            </div>
          )}

          {(isIncomingTransaction ? transaction.counterparty?.bank_name : transaction.counterparty?.bank_name) && (
            <div className='detail-row'>
              <span className='detail-label'>{i18n.getMessage('transaction.label.bankName')}</span>
              <div className='detail-value'>
                <span>{transaction.counterparty?.bank_name}</span>
                <button
                  className='copy-button'
                  onClick={() => handleCopy(transaction.counterparty?.bank_name, 'bankName')}
                >
                  <CopyIcon />
                </button>
              </div>
            </div>
          )}

          {(isIncomingTransaction
            ? transaction.counterparty?.bank_country
            : transaction.counterparty?.bank_country) && (
            <div className='detail-row'>
              <span className='detail-label'>{i18n.getMessage('transaction.label.bankCountry')}</span>
              <div className='detail-value'>
                <span>{i18n.getMessage(`country.${transaction.counterparty?.bank_country}`)}</span>
                <button
                  className='copy-button'
                  onClick={() =>
                    handleCopy(i18n.getMessage(`country.${transaction.counterparty?.bank_country}`), 'bankCountry')
                  }
                >
                  <CopyIcon />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Second Section - Recipient for Incoming, Sender for Outgoing */}
        <div className='details-section mobile'>
          <h3 className='section-title'>
            {i18n.getMessage(
              isIncomingTransaction ? 'transaction.section.recipientDetails' : 'transaction.section.senderDetails'
            )}
          </h3>

          {(isIncomingTransaction
            ? transaction.iban || transaction.crypto_address || transaction.wallet_number
            : transaction.iban || transaction.crypto_address || transaction.wallet_number) && (
            <div className='detail-row'>
              <span className='detail-label'>{i18n.getMessage('transaction.label.accountNumber')}</span>
              <div className='detail-value'>
                <span>{transaction.iban || transaction.crypto_address || transaction.wallet_number}</span>
                <button
                  className='copy-button'
                  onClick={() =>
                    handleCopy(
                      transaction.iban || transaction.crypto_address || transaction.wallet_number,
                      isIncomingTransaction ? 'recipientAccount' : 'senderAccount'
                    )
                  }
                >
                  <CopyIcon />
                </button>
              </div>
            </div>
          )}

          {(isIncomingTransaction ? transaction.recipient_name : transaction.sender_name) && (
            <div className='detail-row'>
              <span className='detail-label'>{i18n.getMessage('transaction.label.name')}</span>
              <div className='detail-value'>
                <span>{isIncomingTransaction ? transaction.recipient_name : transaction.sender_name}</span>
                {isIncomingTransaction && (
                  <button
                    className='copy-button'
                    onClick={() => handleCopy(transaction.recipient_name, 'recipientName')}
                  >
                    <CopyIcon />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Attached Documents Section */}
        {transaction?.documents && transaction.documents.length > 0 && (
          <div className='details-section mobile'>
            <h3 className='section-title'>{i18n.getMessage('transaction.section.attachedDocuments')}</h3>
            <FileDisplay
              files={transaction.documents}
              accountNumber={transaction.account_number}
              transactionNumber={transaction.transaction_number}
            />
          </div>
        )}

        {/* Action Buttons */}
        <div className='action-buttons mobile'>
          <Button
            type='secondary'
            size='small'
            onClick={onPDFClick}
            isDisabled={isReportDisabled}
            className='export-button mobile'
          >
            <ExportIcon className='button-icon' />
            {i18n.getMessage('button.pdf')}
          </Button>
        </div>
      </div>
    </div>
  );

  if (isMobile) {
    return renderMobile();
  }

  return (
    <div className='commission-payment-details'>
      <div className='modal-header'>
        <h2 className='modal-title'>{i18n.getMessage('popUp.paymentDetails')}</h2>
        <CloseIcon className='close-icon' onClick={onClose} />
      </div>

      <div className='modal-body'>
        {/* Transaction Header */}
        <div className='transaction-header'>
          <div className='transaction-type-block'>
            <div className='transaction-type'>
              <div className='type-icon-wrapper'>
                <ArrowUpIcon className='type-icon' />
              </div>
              <div className='type-info'>
                <span className='type-label'>{i18n.getMessage('transaction.type.commission')}</span>
              </div>
            </div>

            <div className='transaction-type'>
              <span className='type-date'>{`${createdDate.date}, ${createdDate.time}`}</span>
            </div>
          </div>

          <div className='transaction-status'>
            <span className={`status-badge ${getStatusBadgeClass(displayStatus)}`}>
              {i18n.getMessage(`transaction.details.status.${displayStatus}`)}
            </span>
            <div
              className={`transaction-amount ${isIncomingTransaction ? 'incoming' : ''} ${isFailedOrRejected ? 'failed' : ''}`}
            >
              {amount}
            </div>
          </div>
        </div>

        <div className='details-container'>
          {/* Transaction Details Section */}
          <div className='details-left-column'>
            <div className='details-section transaction-details-section'>
              <div>
                <div className='section-header'>
                  <h3>{i18n.getMessage('transaction.section.transactionDetails')}</h3>
                </div>
                <div className='section-content'>
                  <div className='detail-row'>
                    <span className='detail-label'>{i18n.getMessage('transaction.label.reference')}</span>
                    <div className='detail-value with-action'>
                      <span>{transaction.comment}</span>
                      <button
                        className='copy-button'
                        onClick={() => handleCopy(transaction.comment, 'reference')}
                        title={i18n.getMessage('button.copy')}
                      >
                        <CopyIcon />
                      </button>
                    </div>
                  </div>

                  {transaction.purpose && (
                    <div className='detail-row'>
                      <span className='detail-label'>{i18n.getMessage('transaction.label.purpose')}</span>
                      <div className='detail-value with-action'>
                        <span>{formattedPurpose}</span>
                        <button
                          className='copy-button'
                          onClick={() => handleCopy(formattedPurpose, 'purpose')}
                          title={i18n.getMessage('button.copy')}
                        >
                          <CopyIcon />
                        </button>
                      </div>
                    </div>
                  )}

                  <div className='detail-row'>
                    <span className='detail-label'>{i18n.getMessage('transaction.label.commission')}</span>
                    <div className='detail-value'>
                      <span>{commission}</span>
                    </div>
                  </div>

                  <div className='detail-row'>
                    <span className='detail-label'>{i18n.getMessage('transaction.label.transactionId')}</span>
                    <div className='detail-value with-action'>
                      <span>{transaction.transaction_number}</span>
                      <button
                        className='copy-button'
                        onClick={() => handleCopy(transaction.transaction_number, 'transactionId')}
                        title={i18n.getMessage('button.copy')}
                      >
                        <CopyIcon />
                      </button>
                    </div>
                  </div>

                  <div className='detail-row'>
                    <span className='detail-label'>{i18n.getMessage('transaction.label.paymentMethod')}</span>
                    <div className='detail-value'>
                      <span>{i18n.getMessage(`transfer.paymentMethod.${transaction.payment_method}`)}</span>
                    </div>
                  </div>

                  <div className='detail-row'>
                    <span className='detail-label'>{i18n.getMessage('transaction.label.dateCreated')}</span>
                    <div className='detail-value'>
                      <span>{`${createdDate.date}, ${createdDate.time}`}</span>
                    </div>
                  </div>

                  {completedDate.date && completedDate.time && (
                    <div className='detail-row'>
                      <span className='detail-label'>{i18n.getMessage('transaction.label.dateCompleted')}</span>
                      <div className='detail-value'>
                        <span>{`${completedDate.date}, ${completedDate.time}`}</span>
                      </div>
                    </div>
                  )}

                  <div className='detail-row'>
                    <span className='detail-label'>{i18n.getMessage('transaction.label.lastUpdated')}</span>
                    <div className='detail-value'>
                      <span>{`${lastUpdatedDate.date}, ${lastUpdatedDate.time}`}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Attached Documents Section */}
              {transaction?.documents && transaction.documents.length > 0 && (
                <div className='details-section'>
                  <div className='section-header'>
                    <h3>{i18n.getMessage('transaction.section.attachedDocuments')}</h3>
                  </div>
                  <div className='section-content'>
                    <FileDisplay
                      files={transaction.documents}
                      accountNumber={transaction.account_number}
                      transactionNumber={transaction.transaction_number}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className='details-right-column'>
            {/* First Section - Sender for Incoming, Recipient for Outgoing */}
            <div className='details-section'>
              <div className='section-header'>
                <h3>
                  {i18n.getMessage(
                    isIncomingTransaction ? 'transaction.section.senderDetails' : 'transaction.section.recipientDetails'
                  )}
                </h3>
              </div>
              <div className='section-content'>
                {(isIncomingTransaction ? transaction.sender_name : transaction.recipient_name) && (
                  <div className='detail-row'>
                    <span className='detail-label'>{i18n.getMessage('transaction.label.name')}</span>
                    <div className='detail-value with-action'>
                      <span>{isIncomingTransaction ? transaction.sender_name : transaction.recipient_name}</span>
                      <button
                        className='copy-button'
                        onClick={() =>
                          handleCopy(
                            isIncomingTransaction ? transaction.sender_name : transaction.recipient_name,
                            isIncomingTransaction ? 'senderName' : 'recipientName'
                          )
                        }
                        title={i18n.getMessage('button.copy')}
                      >
                        <CopyIcon />
                      </button>
                    </div>
                  </div>
                )}

                {(isIncomingTransaction
                  ? transaction.counterparty?.bank_name
                  : transaction.counterparty?.bank_name) && (
                  <div className='detail-row'>
                    <span className='detail-label'>{i18n.getMessage('transaction.label.bankName')}</span>
                    <div className='detail-value with-action'>
                      <span>{transaction.counterparty?.bank_name}</span>
                      <button
                        className='copy-button'
                        onClick={() => handleCopy(transaction.counterparty?.bank_name, 'bankName')}
                        title={i18n.getMessage('button.copy')}
                      >
                        <CopyIcon />
                      </button>
                    </div>
                  </div>
                )}

                {(isIncomingTransaction ? transaction.counterparty?.bic : transaction.counterparty?.bic) && (
                  <div className='detail-row'>
                    <span className='detail-label'>{i18n.getMessage('transaction.label.bic')}</span>
                    <div className='detail-value with-action'>
                      <span>{transaction.counterparty?.bic}</span>
                      <button
                        className='copy-button'
                        onClick={() => handleCopy(transaction.counterparty?.bic, 'bic')}
                        title={i18n.getMessage('button.copy')}
                      >
                        <CopyIcon />
                      </button>
                    </div>
                  </div>
                )}

                {(isIncomingTransaction
                  ? transaction.counterparty?.bank_country
                  : transaction.counterparty?.bank_country) && (
                  <div className='detail-row'>
                    <span className='detail-label'>{i18n.getMessage('transaction.label.bankCountry')}</span>
                    <div className='detail-value with-action'>
                      <span>{i18n.getMessage(`country.${transaction.counterparty?.bank_country}`)}</span>
                      <button
                        className='copy-button'
                        onClick={() =>
                          handleCopy(
                            i18n.getMessage(`country.${transaction.counterparty?.bank_country}`),
                            'bankCountry'
                          )
                        }
                        title={i18n.getMessage('button.copy')}
                      >
                        <CopyIcon />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Second Section - Recipient for Incoming, Sender for Outgoing */}
            <div className='details-section'>
              <div className='section-header'>
                <h3>
                  {i18n.getMessage(
                    isIncomingTransaction ? 'transaction.section.recipientDetails' : 'transaction.section.senderDetails'
                  )}
                </h3>
              </div>
              <div className='section-content'>
                {(isIncomingTransaction
                  ? transaction.iban || transaction.crypto_address || transaction.wallet_number
                  : transaction.iban || transaction.crypto_address || transaction.wallet_number) && (
                  <div className='detail-row'>
                    <span className='detail-label'>{i18n.getMessage('transaction.label.accountNumber')}</span>
                    <div className='detail-value break-all'>
                      <span>{transaction.iban || transaction.crypto_address || transaction.wallet_number}</span>
                      <button
                        className='copy-button'
                        onClick={() =>
                          handleCopy(
                            transaction.iban || transaction.crypto_address || transaction.wallet_number,
                            isIncomingTransaction ? 'recipientAccount' : 'senderAccount'
                          )
                        }
                        title={i18n.getMessage('button.copy')}
                      >
                        <CopyIcon />
                      </button>
                    </div>
                  </div>
                )}

                {(isIncomingTransaction ? transaction.recipient_name : transaction.sender_name) && (
                  <div className='detail-row'>
                    <span className='detail-label'>{i18n.getMessage('transaction.label.name')}</span>
                    <div className='detail-value with-action'>
                      <span>{isIncomingTransaction ? transaction.recipient_name : transaction.sender_name}</span>
                      {isIncomingTransaction && (
                        <button
                          className='copy-button'
                          onClick={() => handleCopy(transaction.recipient_name, 'recipientName')}
                          title={i18n.getMessage('button.copy')}
                        >
                          <CopyIcon />
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className='action-buttons'>
          <Button
            type='secondary'
            size='large'
            onClick={onPDFClick}
            isDisabled={isReportDisabled}
            className='export-button'
          >
            <ExportIcon className='button-icon' />
            {i18n.getMessage('button.exportPdf')}
          </Button>
        </div>
      </div>
    </div>
  );
};

CommissionPaymentDetails.propTypes = {
  transaction: PropTypes.object.isRequired,
  amountDetails: PropTypes.object,
  commissionDetails: PropTypes.object,
  totalAmountDetails: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  userAccounts: PropTypes.array,
  transactionStore: MobXPropTypes.observableObject,
  customerPermissions: PropTypes.array,
  accountNumber: PropTypes.string
};

export default inject((stores) => ({
  accountNumber: stores.userStore.userData.account?.account_number,
  userAccounts: stores.userStore?.userAccounts,
  transactionStore: stores.transactionsStore,
  customerPermissions: stores.userStore?.currentCustomerPermissions,
  userStore: stores.userStore
}))(observer(CommissionPaymentDetails));
